import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateTriggerProfile } from '@/lib/api/generated/api';
import type {
  TriggerProfileUpdate,
  TriggerProfileView,
} from '@/lib/api/generated/model';

import { toTriggerCreateError } from '../errors';
import { triggerKeys } from '../queryKeys';

/**
 * General partial edit of an existing Trigger Profile of any driver type
 * (spec 074, FR-001/FR-002) — name, description, config, connection, and
 * schedule fields, without recreating the profile. `type` cannot be changed:
 * `TriggerProfileUpdate` (the generated wire shape, taken directly as the
 * mutation variable, mirroring `useUpdateConnection`'s own convention) has no
 * `type` field at all, so the type system rules it out rather than a runtime
 * check.
 *
 * Calls the same shared `PATCH` endpoint `useSetTriggerProfileEnabled`/
 * `useUpdateTriggerProfileSchedule` call, with the third branch of its
 * backend `anyOf` request body (research.md D3's reasoning for why these are
 * separate hooks rather than one with a discriminated parameter still
 * applies: the body has no discriminator field, so a caller sends exactly
 * one shape or another, never a mix).
 *
 * Invalidates both the list (a changed name/enabled-adjacent row) and the
 * detail read, matching every other trigger-profile mutation's own
 * invalidation pair.
 */
export function useUpdateTriggerProfile(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      triggerProfileId,
      data,
    }: {
      triggerProfileId: string;
      data: TriggerProfileUpdate;
    }): Promise<TriggerProfileView> => {
      const response = await updateTriggerProfile(
        workspaceId,
        triggerProfileId,
        data,
      );
      if (response.status === 200) return response.data;
      throw toTriggerCreateError(response.status, response.data);
    },
    onSuccess: (_profile, variables) => {
      void queryClient.invalidateQueries({
        queryKey: triggerKeys.list(workspaceId),
      });
      void queryClient.invalidateQueries({
        queryKey: triggerKeys.detail(workspaceId, variables.triggerProfileId),
      });
    },
  });
}
